import { createContext, useContext, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import { useMutation, useQueryClient } from '@zg-rentals/react-query5';
import type { IncomeDocumentsAnswers, PendingPick, PickedRow } from '../utils/incomeDocuments';
import { getRowDocumentIds } from '../utils/incomeDocuments';
import { INCOME_DOCUMENT_UPLOAD_KEY, INCOME_DOCUMENT_UPLOAD_SCOPE } from './useIncomeDocumentUploads';
import { useAttachRequiredDocuments } from './useRequiredDocumentMutations';
import { fetchLatestApplication } from './useRentalApplication';

let nextKey = 0;

function keepUnlisted(answers: IncomeDocumentsAnswers | null | undefined, picks: Array<PendingPick>) {
  return picks.flatMap((pick) => {
    const listedIds = getRowDocumentIds(answers, pick);
    const documentIds = pick.documentIds.filter((id) => !listedIds.has(id));
    return documentIds.length > 0 ? [{ ...pick, documentIds }] : [];
  });
}

/**
 * Shows picked files in their row at once, and keeps their card menu locked until the refetched application
 * lists them. A failed attach keeps only the ids that the refetched application lists
 */
export function usePendingDocumentPicks(applicationId: string, answers: IncomeDocumentsAnswers | null | undefined) {
  const queryClient = useQueryClient();
  const { mutateAsync: attachRequiredDocuments } = useAttachRequiredDocuments();
  const [entries, setEntries] = useState<Array<PendingPick>>([]);
  const [prunedForAnswers, setPrunedForAnswers] = useState(answers);

  if (answers !== prunedForAnswers) {
    setPrunedForAnswers(answers);
    setEntries((current) => keepUnlisted(answers, current));
  }

  const { mutate: attachPick } = useMutation({
    mutationKey: INCOME_DOCUMENT_UPLOAD_KEY,
    scope: INCOME_DOCUMENT_UPLOAD_SCOPE,
    meta: { context: { api: 'cappsRenterDocuments', details: 'attachPickedDocuments' } },
    mutationFn: async (pick: PendingPick) => {
      try {
        await attachRequiredDocuments({
          postData: {
            applicationId,
            documentType: pick.documentType,
            incomeId: pick.incomeId,
            documentIds: pick.documentIds,
          },
        });
        return;
      } catch {
        // capps writes the links before it reads the files back, so a failed attach can still be stored
      }
      const application = await fetchLatestApplication(queryClient, applicationId).catch(() => undefined);
      const storedIds = getRowDocumentIds(application?.answers, pick);
      setEntries((current) =>
        current.flatMap((entry) => {
          if (entry.key !== pick.key) {
            return [entry];
          }
          const documentIds = entry.documentIds.filter((id) => storedIds.has(id));
          return documentIds.length > 0 ? [{ ...entry, documentIds }] : [];
        }),
      );
    },
  });

  const pick = useMemoizedFn((row: PickedRow, documentIds: Array<string>) => {
    const entry: PendingPick = { ...row, key: `pick-${(nextKey += 1)}`, documentIds };
    setEntries((current) => [...current, entry]);
    attachPick(entry);
  });

  const isPicking = (documentId: string) => entries.some(({ documentIds }) => documentIds.includes(documentId));

  return { entries, pick, isPicking };
}

type PendingPickActions = Pick<ReturnType<typeof usePendingDocumentPicks>, 'isPicking'>;

export const PendingPicksContext = createContext<PendingPickActions | undefined>(undefined);

export function usePendingPickActions() {
  const actions = useContext(PendingPicksContext);
  if (!actions) {
    throw new Error('usePendingPickActions needs a PendingPicksContext provider');
  }
  return actions;
}
